import { useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Pencil,
  Send,
  Printer,
  Copy,
  Check,
  X as XIcon,
  Clock,
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  FileText,
  ExternalLink,
} from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import toast from "react-hot-toast";
import {
  Button,
  Card,
  StatusBadge,
  ConfirmModal,
  Modal,
} from "@/components/ui";
import { budgetsApi } from "@/services/api";
import BudgetPrintView from "@/components/features/BudgetPrintView";

export default function BudgetViewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const printRef = useRef<HTMLDivElement>(null);

  const [showSendModal, setShowSendModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["budget", id],
    queryFn: () => budgetsApi.get(id!),
  });

  const sendMutation = useMutation({
    mutationFn: () => budgetsApi.send(id!),
    onSuccess: () => {
      toast.success("Orçamento enviado com sucesso");
      queryClient.invalidateQueries({ queryKey: ["budget", id] });
      setShowSendModal(false);
    },
    onError: () => {
      toast.error("Erro ao enviar orçamento");
    },
  });

  const approveMutation = useMutation({
    mutationFn: () => budgetsApi.approve(id!),
    onSuccess: () => {
      toast.success("Orçamento aprovado");
      queryClient.invalidateQueries({ queryKey: ["budget", id] });
    },
    onError: () => {
      toast.error("Erro ao aprovar orçamento");
    },
  });

  const rejectMutation = useMutation({
    mutationFn: () => budgetsApi.reject(id!, rejectReason),
    onSuccess: () => {
      toast.success("Orçamento rejeitado");
      queryClient.invalidateQueries({ queryKey: ["budget", id] });
      setShowRejectModal(false);
      setRejectReason("");
    },
    onError: () => {
      toast.error("Erro ao rejeitar orçamento");
    },
  });

  const budget = data?.data;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const copyPublicLink = () => {
    if (budget?.public_token) {
      const link = `${window.location.origin}/p/${budget.public_token}`;
      navigator.clipboard.writeText(link);
      toast.success("Link copiado para a área de transferência");
    }
  };

  const handlePrint = () => {
    setShowPrintModal(true);
  };

  const executePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      toast.error("Pop-up bloqueado. Permita pop-ups para imprimir.");
      return;
    }

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Orçamento ${budget?.number || ""}</title>
          <style>
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: Arial, sans-serif; color: #1a1a1a; line-height: 1.5; }
            .print-area { padding: 20mm; max-width: 210mm; margin: 0 auto; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f5f5f5; font-weight: bold; }
            .text-right { text-align: right; }
            .text-center { text-align: center; }
            .font-mono { font-family: monospace; }
            .font-bold { font-weight: bold; }
            .font-semibold { font-weight: 600; }
            .text-sm { font-size: 0.875rem; }
            .text-lg { font-size: 1.125rem; }
            .text-xl { font-size: 1.25rem; }
            .text-2xl { font-size: 1.5rem; }
            .text-3xl { font-size: 1.875rem; }
            .mb-2 { margin-bottom: 0.5rem; }
            .mb-3 { margin-bottom: 0.75rem; }
            .mb-6 { margin-bottom: 1.5rem; }
            .mb-8 { margin-bottom: 2rem; }
            .mt-2 { margin-top: 0.5rem; }
            .mt-12 { margin-top: 3rem; }
            .py-2 { padding-top: 0.5rem; padding-bottom: 0.5rem; }
            .py-3 { padding-top: 0.75rem; padding-bottom: 0.75rem; }
            .px-3 { padding-left: 0.75rem; padding-right: 0.75rem; }
            .px-4 { padding-left: 1rem; padding-right: 1rem; }
            .p-4 { padding: 1rem; }
            .p-8 { padding: 2rem; }
            .pb-6 { padding-bottom: 1.5rem; }
            .pt-6 { padding-top: 1.5rem; }
            .border-b { border-bottom: 1px solid #e5e7eb; }
            .border-b-2 { border-bottom: 2px solid #d1d5db; }
            .border-t { border-top: 1px solid #d1d5db; }
            .rounded-lg { border-radius: 0.5rem; }
            .rounded-full { border-radius: 9999px; }
            .bg-gray-50 { background-color: #f9fafb; }
            .bg-gray-100 { background-color: #f3f4f6; }
            .text-gray-500 { color: #6b7280; }
            .text-gray-600 { color: #4b5563; }
            .text-gray-700 { color: #374151; }
            .text-gray-800 { color: #1f2937; }
            .text-gray-900 { color: #111827; }
            .text-red-600 { color: #dc2626; }
            .flex { display: flex; }
            .justify-between { justify-content: space-between; }
            .justify-end { justify-content: end; }
            .items-start { align-items: flex-start; }
            .grid { display: grid; }
            .grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
            .gap-4 { gap: 1rem; }
            .w-72 { width: 18rem; }
            .whitespace-pre-wrap { white-space: pre-wrap; }
            .inline-block { display: inline-block; }
            @media print {
              body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
              @page { size: A4; margin: 10mm; }
            }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);

    setShowPrintModal(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (error || !budget) {
    return (
      <div className="text-center py-12">
        <p className="text-text-muted mb-4">Orçamento não encontrado</p>
        <Button onClick={() => navigate("/admin/budgets")}>Voltar</Button>
      </div>
    );
  }

  const canEdit = budget.status === "draft";
  const canSend = budget.status === "draft";
  const canApproveReject = ["sent", "viewed"].includes(budget.status);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/admin/budgets")}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold text-text-primary font-mono">
                {budget.number}
              </h1>
              <StatusBadge status={budget.status} />
            </div>
            <p className="text-text-secondary mt-1">
              Criado em{" "}
              {format(new Date(budget.created_at), "dd 'de' MMMM 'de' yyyy", {
                locale: ptBR,
              })}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {budget.public_token && (
            <Button variant="secondary" size="sm" onClick={copyPublicLink}>
              <Copy className="w-4 h-4 mr-2" />
              Copiar Link
            </Button>
          )}
          <Button variant="secondary" size="sm" onClick={handlePrint}>
            <Printer className="w-4 h-4 mr-2" />
            Imprimir
          </Button>
          {canEdit && (
            <Link to={`/admin/budgets/${id}/edit`}>
              <Button variant="secondary" size="sm">
                <Pencil className="w-4 h-4 mr-2" />
                Editar
              </Button>
            </Link>
          )}
          {canSend && (
            <Button size="sm" onClick={() => setShowSendModal(true)}>
              <Send className="w-4 h-4 mr-2" />
              Enviar
            </Button>
          )}
          {canApproveReject && (
            <>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowRejectModal(true)}
                className="border-red-500/50 text-red-400 hover:bg-red-500/10"
              >
                <XIcon className="w-4 h-4 mr-2" />
                Rejeitar
              </Button>
              <Button
                size="sm"
                onClick={() => approveMutation.mutate()}
                isLoading={approveMutation.isPending}
                className="bg-green-600 hover:bg-green-500"
              >
                <Check className="w-4 h-4 mr-2" />
                Aprovar
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Client Info */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-primary" />
              Cliente
            </h2>
            {budget.client ? (
              <div className="space-y-3">
                <p className="text-xl font-medium text-text-primary">
                  {budget.client.name}
                </p>
                {budget.client.trade_name && (
                  <p className="text-text-secondary">
                    {budget.client.trade_name}
                  </p>
                )}
                <div className="flex flex-wrap gap-4 text-sm text-text-muted">
                  {budget.client.email && (
                    <span className="flex items-center gap-1">
                      <Mail className="w-4 h-4" />
                      {budget.client.email}
                    </span>
                  )}
                  {budget.client.phone && (
                    <span className="flex items-center gap-1">
                      <Phone className="w-4 h-4" />
                      {budget.client.phone}
                    </span>
                  )}
                </div>
                {budget.client.address_city && (
                  <p className="flex items-center gap-1 text-sm text-text-muted">
                    <MapPin className="w-4 h-4" />
                    {[
                      budget.client.address_street,
                      budget.client.address_number,
                      budget.client.address_neighborhood,
                      budget.client.address_city,
                      budget.client.address_state,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-text-muted">Cliente não informado</p>
            )}
          </Card>

          {/* Budget Details */}
          {(budget.title || budget.description) && (
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                Detalhes
              </h2>
              {budget.title && (
                <h3 className="text-xl font-medium text-text-primary mb-2">
                  {budget.title}
                </h3>
              )}
              {budget.description && (
                <p className="text-text-secondary whitespace-pre-wrap">
                  {budget.description}
                </p>
              )}
            </Card>
          )}

          {/* Items */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-text-primary mb-4">
              Itens
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-3 px-4 text-text-secondary font-medium">
                      Descrição
                    </th>
                    <th className="text-right py-3 px-4 text-text-secondary font-medium">
                      Qtd
                    </th>
                    <th className="text-right py-3 px-4 text-text-secondary font-medium">
                      Valor Unit.
                    </th>
                    <th className="text-right py-3 px-4 text-text-secondary font-medium">
                      Desconto
                    </th>
                    <th className="text-right py-3 px-4 text-text-secondary font-medium">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {budget.items?.map((item: any, index: number) => {
                    const itemTotal = item.quantity * item.unit_price;
                    const discountValue = item.discount_value || 0;
                    const discount =
                      item.discount_type === "percent"
                        ? itemTotal * (discountValue / 100)
                        : discountValue;
                    const total = item.total || itemTotal - discount;

                    return (
                      <tr
                        key={item.id || index}
                        className="border-b border-white/5"
                      >
                        <td className="py-4 px-4">
                          <p className="text-text-primary">
                            {item.description}
                          </p>
                          {item.product?.name &&
                            item.product.name !== item.description && (
                              <p className="text-sm text-text-muted">
                                {item.product.name}
                              </p>
                            )}
                        </td>
                        <td className="py-4 px-4 text-right font-mono text-text-secondary">
                          {item.quantity} {item.unit}
                        </td>
                        <td className="py-4 px-4 text-right font-mono text-text-secondary">
                          {formatCurrency(item.unit_price)}
                        </td>
                        <td className="py-4 px-4 text-right font-mono text-red-400">
                          {item.discount_value > 0
                            ? item.discount_type === "percent"
                              ? `-${item.discount_value}%`
                              : `-${formatCurrency(item.discount_value)}`
                            : "-"}
                        </td>
                        <td className="py-4 px-4 text-right font-mono font-medium text-text-primary">
                          {formatCurrency(total)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="mt-6 pt-6 border-t border-white/10 space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-text-secondary">Subtotal</span>
                <span className="font-mono text-text-primary">
                  {formatCurrency(budget.subtotal || 0)}
                </span>
              </div>
              {budget.discount_value > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-text-secondary">
                    Desconto{" "}
                    {budget.discount_type === "percent"
                      ? `(${budget.discount_value}%)`
                      : ""}
                  </span>
                  <span className="font-mono text-red-400">
                    -
                    {formatCurrency(
                      budget.discount_type === "percent"
                        ? (budget.subtotal || 0) * (budget.discount_value / 100)
                        : budget.discount_value,
                    )}
                  </span>
                </div>
              )}
              <div className="flex justify-between items-center pt-2 border-t border-white/10">
                <span className="text-lg font-semibold text-text-primary">
                  Total
                </span>
                <span className="font-mono text-2xl font-bold text-primary">
                  {formatCurrency(budget.total || 0)}
                </span>
              </div>
            </div>
          </Card>

          {/* Terms */}
          {(budget.payment_terms ||
            budget.delivery_time ||
            budget.warranty) && (
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-text-primary mb-4">
                Condições
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {budget.payment_terms && (
                  <div>
                    <h4 className="text-sm font-medium text-text-secondary mb-2">
                      Pagamento
                    </h4>
                    <p className="text-text-primary whitespace-pre-wrap">
                      {budget.payment_terms}
                    </p>
                  </div>
                )}
                {budget.delivery_time && (
                  <div>
                    <h4 className="text-sm font-medium text-text-secondary mb-2">
                      Prazo de Entrega
                    </h4>
                    <p className="text-text-primary whitespace-pre-wrap">
                      {budget.delivery_time}
                    </p>
                  </div>
                )}
                {budget.warranty && (
                  <div>
                    <h4 className="text-sm font-medium text-text-secondary mb-2">
                      Garantia
                    </h4>
                    <p className="text-text-primary whitespace-pre-wrap">
                      {budget.warranty}
                    </p>
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Notes */}
          {budget.notes && (
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-text-primary mb-4">
                Observações
              </h2>
              <p className="text-text-secondary whitespace-pre-wrap">
                {budget.notes}
              </p>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Info */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-text-primary mb-4">
              Informações
            </h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-text-muted flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Validade
                </span>
                <span className="text-text-primary">
                  {budget.validity_date
                    ? format(new Date(budget.validity_date), "dd/MM/yyyy")
                    : "Não definida"}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-text-muted flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Criado em
                </span>
                <span className="text-text-primary">
                  {format(new Date(budget.created_at), "dd/MM/yyyy HH:mm")}
                </span>
              </div>
              {budget.sent_at && (
                <div className="flex items-center justify-between">
                  <span className="text-text-muted flex items-center gap-2">
                    <Send className="w-4 h-4" />
                    Enviado em
                  </span>
                  <span className="text-text-primary">
                    {format(new Date(budget.sent_at), "dd/MM/yyyy HH:mm")}
                  </span>
                </div>
              )}
              {budget.viewed_at && (
                <div className="flex items-center justify-between">
                  <span className="text-text-muted flex items-center gap-2">
                    <ExternalLink className="w-4 h-4" />
                    Visualizado em
                  </span>
                  <span className="text-text-primary">
                    {format(new Date(budget.viewed_at), "dd/MM/yyyy HH:mm")}
                  </span>
                </div>
              )}
            </div>
          </Card>

          {/* Internal Notes */}
          {budget.internal_notes && (
            <Card className="p-6 border-yellow-500/30 bg-yellow-500/5">
              <h2 className="text-lg font-semibold text-yellow-400 mb-2">
                Notas Internas
              </h2>
              <p className="text-text-secondary text-sm whitespace-pre-wrap">
                {budget.internal_notes}
              </p>
            </Card>
          )}

          {/* History */}
          {budget.history && budget.history.length > 0 && (
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-text-primary mb-4">
                Histórico
              </h2>
              <div className="space-y-3">
                {budget.history.map((entry: any, index: number) => (
                  <div key={index} className="flex gap-3 text-sm">
                    <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                    <div>
                      <p className="text-text-primary">{entry.description}</p>
                      <p className="text-text-muted text-xs">
                        {format(new Date(entry.created_at), "dd/MM/yyyy HH:mm")}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Send Confirmation Modal */}
      <ConfirmModal
        isOpen={showSendModal}
        onClose={() => setShowSendModal(false)}
        onConfirm={() => sendMutation.mutate()}
        title="Enviar Orçamento"
        message="Tem certeza que deseja enviar este orçamento? O cliente receberá um link para visualizar e aprovar."
        confirmText="Enviar"
        variant="info"
        isLoading={sendMutation.isPending}
      />

      {/* Reject Modal */}
      <Modal
        isOpen={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        title="Rejeitar Orçamento"
      >
        <div className="space-y-4">
          <p className="text-text-secondary">
            Informe o motivo da rejeição (opcional):
          </p>
          <textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Motivo da rejeição..."
            className="w-full h-24 px-4 py-3 bg-dark-lighter border border-white/10 rounded-lg text-text-primary placeholder-text-muted focus:outline-none focus:border-primary resize-none"
          />
          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => setShowRejectModal(false)}
            >
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={() => rejectMutation.mutate()}
              isLoading={rejectMutation.isPending}
            >
              Rejeitar Orçamento
            </Button>
          </div>
        </div>
      </Modal>

      {/* Print Preview Modal */}
      <Modal
        isOpen={showPrintModal}
        onClose={() => setShowPrintModal(false)}
        title="Pré-visualização de Impressão"
        size="xl"
      >
        <div className="space-y-4">
          <div className="max-h-[60vh] overflow-y-auto bg-white rounded-lg">
            <BudgetPrintView ref={printRef} budget={budget} />
          </div>
          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => setShowPrintModal(false)}
            >
              Cancelar
            </Button>
            <Button onClick={executePrint}>
              <Printer className="w-4 h-4 mr-2" />
              Imprimir / Salvar PDF
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
